<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.customerCourse.actions.move.title')"
    :info="$t('components.customerCourse.actions.move.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.customerCourse.actions.move.confirmButton'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-alert
        show
        variant="info"
        class="text-left text-sm mb-4"
      >
        {{ $t('components.customerCourse.actions.move.dateNotice') }}
      </o-alert>

      <div v-if="loadingData" class="flex items-center justify-center py-8">
        <o-loader class="h-8 w-8" />
      </div>

      <template v-else-if="loadError">
        <p class="text-sm text-gray-500 text-center py-4">
          {{ $t('components.customerCourse.actions.move.loadError') }}
        </p>
      </template>

      <o-field-group
        v-else
        :label="$t('components.customerCourse.actions.move.selectRun')"
        :info="$t('components.customerCourse.actions.move.selectRunInfo')"
        class="block"
      >
        <div class="border rounded-lg dark:border-gray-800 overflow-hidden max-h-60 overflow-y-auto">
          <template v-if="courseRuns.length === 0">
            <p class="text-sm text-gray-500 text-center py-4">
              {{ $t('components.customerCourse.actions.move.noAvailable') }}
            </p>
          </template>
          <label
            v-for="run in courseRuns"
            :key="run.idCourseRun"
            :class="[
              'flex items-center p-3 cursor-pointer border-b dark:border-gray-800 last:border-none',
              { 'bg-primary-50 dark:bg-primary-900': selectedCourseRun === run.idCourseRun }
            ]"
          >
            <input
              v-model="selectedCourseRun"
              :value="run.idCourseRun"
              type="radio"
              class="mr-3"
            >
            <div>
              <p class="text-sm font-medium">
                {{ run.course?.name }}<template v-if="run.information"> - {{ run.information }}</template>
              </p>
              <p v-if="run.dates && run.dates[0]" class="text-xs text-gray-500">
                {{ $t('components.customerCourse.actions.move.starts', { date: $date.convert(run.dates[0], undefined, $date.presets.long) }) }}
              </p>
            </div>
          </label>
        </div>
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script>
import { OFieldGroup, OAlert, OLoader } from '@/components/elements'

export default {
  components: {
    OFieldGroup,
    OAlert,
    OLoader,
  },
  props: {
    portal: {
      type: String,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      customerCourse: null,
      loadingData: false,
      loadError: false,
      courseRuns: [],
      selectedCourseRun: null,
    }
  },
  methods: {
    async open (customerCourse) {
      this.selectedCourseRun = null
      this.loadError = false
      this.courseRuns = []

      this.$refs.dialog.open()

      // If customerCourse doesn't have model methods, fetch the full model
      if (typeof customerCourse.objectID !== 'function') {
        this.loadingData = true
        try {
          const response = await this.$api.customerCourse.get(customerCourse.idCustomerCourse)
          this.customerCourse = response.data
        } catch (e) {
          console.error(e)
          this.$notification.error({ errors: e?.errors })
          this.loadError = true
          this.loadingData = false
          return
        }
      } else {
        this.customerCourse = customerCourse
      }

      await this.fetchCourseRuns()
    },
    async fetchCourseRuns () {
      this.loadingData = true
      this.loadError = false

      try {
        const response = await this.$api.course.listCourseRuns({
          page: 1,
          filters: {
            status: 'active',
            when: 'current_upcoming',
          },
        })

        // A customer can be moved to any course, so all runs are offered
        // except the one they are already in.
        const currentRunId = this.customerCourse.courseRun?.idCourseRun
        this.courseRuns = (response.items || []).filter(run => run.idCourseRun !== currentRunId)
      } catch (e) {
        console.error(e)
        this.$notification.error({ errors: e?.errors })
        this.loadError = true
      } finally {
        this.loadingData = false
      }
    },
    validate () {
      return !this.loadError && this.selectedCourseRun !== null
    },
    async confirm () {
      this.$emit('update:loading', true)

      try {
        const response = await this.$api.customerCourse.move(this.customerCourse, {
          idCourseRun: this.selectedCourseRun,
        })
        const customerCourse = response.data

        if (customerCourse) {
          this.$emit('success', customerCourse)

          this.$bus.emit.updated('CustomerCourse', customerCourse)

          // Mirror the destination label shown in the run picker: "{course} - {information}".
          const destinationRun = this.courseRuns.find(run => run.idCourseRun === this.selectedCourseRun)
          const customerName = customerCourse.customer?.displayName || this.customerCourse.customer?.displayName
          const courseName = destinationRun?.course?.name || customerCourse.courseRun?.course?.name
          const runInformation = destinationRun?.information ?? customerCourse.courseRun?.information
          const courseLabel = [courseName, runInformation].filter(Boolean).join(' - ')

          this.$notification.toast({
            title: this.$t('components.customerCourse.actions.move.success'),
            text: this.$t('components.customerCourse.actions.move.successInfo', { name: customerName, course: courseLabel }),
            variant: 'success',
          })

          this.$emit('update:loading', false)
          this.$refs.dialog.close()

          return true
        }
      } catch (e) {
        console.error(e)
        this.$notification.error({ errors: e?.errors })
      }

      this.$emit('update:loading', false)
    },
  },
}
</script>
